import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { FRAME_IMAGE_FIELDS, frameImageVariantConfig } from '../../util/cardImageVariants';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transactionLineItems, postWebsiteInquiry } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
} from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { isListingUnavailable } from '../../util/listingAvailability';

const { UUID } = sdkTypes;
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Async Thunks ================ //

//////////////////
// Show Listing //
//////////////////
// The show-listing params — ONE builder, so Story 7.18's owner swap
// (`fetchOwnUnavailableListingThunk`) issues `ownListings.show` with EXACTLY the
// params `showListingPayloadCreator` issues (ID-8). Body byte-for-byte from the
// creator; only the `config` destructuring moved in with it.
const showListingParams = (listingId, config) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };
};

const showListingPayloadCreator = ({ listingId, config, isOwn = false }, thunkAPI) => {
  const { dispatch, getState, rejectWithValue, requestId, extra: sdk } = thunkAPI;
  const { aspectWidth = 1, aspectHeight = 1 } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = showListingParams(listingId, config);

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      // Story 7.18 (ID-2; S3, S4, D4, D5) — decided HERE, live, on every load,
      // and only on the non-own path: a withdrawn horse (natively closed, not
      // sold) NEVER enters the store for a non-owner, so nothing identifying
      // can reach the DOM, the head or the serialized SSR state. SSR cannot see
      // the owner (fetchCurrentUser above is lazy), so the SSR decision is
      // always the non-owner one — it fails closed; the owner's client swaps
      // to the full view through `fetchOwnUnavailableListingThunk` (ID-8).
      // Nothing is persisted: no cookie, no localStorage, no tph-api row.
      const listing = data?.data?.data;
      const attrs = listing?.attributes || {};
      if (
        !isOwn &&
        isListingUnavailable({ state: attrs.state, listingStatus: attrs.publicData?.listingStatus })
      ) {
        // S5 — the closed listing's discipline, price, option and currency are
        // read from the in-memory response ONLY, to choose matches. They are
        // locals; no action payload, prop or DOM node ever carries them.
        // The available path below stays synchronous inside this `.then` so its
        // action ordering is byte-identical to before 7.18; only this branch
        // returns a promise.
        const publicData = attrs.publicData || {};
        return querySimilarForUnavailable(sdk, {
          listingId: listing.id.uuid,
          disciplines: publicData.disciplines,
          price: attrs.price,
          pricingOption: publicData.pricingOption,
          currency: publicData.pricingCurrency ?? attrs.price?.currency,
          aspectRatio,
        }).then(({ listings: similar, responses }) => {
          // The similar horses are live, published, public listings — storing
          // them is the established pattern and not a D4 concern.
          responses.forEach(r => dispatch(addMarketplaceEntities(r, sanitizeConfig)));
          dispatch(
            listingPageSlice.actions.setUnavailableListing({
              id: listing.id,
              similarIds: similar.map(l => l.id),
              ownerView: false,
              // Step-6 triage (L2, ID-34) — a one-shot marker that this decision
              // was made by the SSR read. The browser's first loadData for the
              // same id consumes it instead of re-reading the closed listing.
              decidedOnServer: typeof window === 'undefined',
              // Final fix round (F1, ID-52) — the request this decision came
              // from; the reducer applies it only for the latest request.
              requestId,
            })
          );
          return { unavailable: true, id: listing.id };
        });
      }

      // Final fix round (F1, ID-53) — only the latest request stores its entity.
      // An older read of the same listing settling last must not put a stale
      // version (a horse withdrawn since) back into the store.
      if (getState().ListingPage?.latestRequestId === requestId) {
        dispatch(addMarketplaceEntities(data, sanitizeConfig));
      }
      return data;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const showListingThunk = createAsyncThunk(
  'ListingPage/showListing',
  showListingPayloadCreator
);
// Backward compatible wrapper for the thunk
export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  return dispatch(showListingThunk({ listingId, config, isOwn })).unwrap();
};

/**
 * Story 7.18 (ID-8, D5) — the OWNER SWAP. SSR renders the unavailable view for
 * everyone (it cannot see the owner); once the client knows who is signed in,
 * each page variant dispatches this with the listing id. It calls
 * `sdk.ownListings.show` with the SAME params as `showListingPayloadCreator`;
 * on success the own listing enters the store and `unavailableListing.ownerView`
 * flips, so the page renders the full owner view.
 *
 * 🔴 ON ANY REJECTION — a non-owner's 403/404 — IT SWALLOWS. It must never
 * touch `showListingError`, which would render NotFoundPage over the
 * unavailable view for every signed-in non-owner. That is why this is NOT
 * `showListingThunk` with `isOwn: true`.
 */
export const fetchOwnUnavailableListingThunk = createAsyncThunk(
  'ListingPage/fetchOwnUnavailableListing',
  ({ listingId, config }, { dispatch, extra: sdk }) => {
    const listingFields = config?.listing?.listingFields;
    const sanitizeConfig = { listingFields };
    return sdk.ownListings
      .show(showListingParams(listingId, config))
      .then(data => {
        dispatch(addMarketplaceEntities(data, sanitizeConfig));
        dispatch(listingPageSlice.actions.setUnavailableOwnerView({ id: data.data.data.id }));
        return { ownerView: true, id: data.data.data.id };
      })
      .catch(() => {
        return { ownerView: false };
      });
  }
);

///////////////////
// Fetch Reviews //
///////////////////
export const fetchReviewsThunk = createAsyncThunk(
  'ListingPage/fetchReviews',
  ({ listingId }, { rejectWithValue, extra: sdk }) => {
    return sdk.reviews
      .query({
        listing_id: listingId,
        state: 'public',
        include: ['author', 'author.profileImage'],
        'fields.image': ['variants.square-small', 'variants.square-small2x'],
      })
      .then(response => {
        return denormalisedResponseEntities(response);
      })
      .catch(e => {
        return rejectWithValue(storableError(e));
      });
  }
);

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  return dispatch(fetchReviewsThunk({ listingId })).unwrap();
};

//////////////////////
// Fetch Time Slots //
//////////////////////

const timeSlotsRequest = createAsyncThunk(
  'ListingPage/timeSlotsRequest',
  (params, { extra: sdk }) => {
    return sdk.timeslots.query(params).then(response => {
      return denormalisedResponseEntities(response);
    });
  }
);

const fetchTimeSlotsPayloadCreator = ({ listingId, start, end, timeZone, options }, thunkAPI) => {
  const { dispatch, getState } = thunkAPI;
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  } else {
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(response => {
        return response.payload;
      })
      .catch(e => {
        return [];
      });
  }
};

export const fetchTimeSlotsThunk = createAsyncThunk(
  'ListingPage/fetchTimeSlots',
  fetchTimeSlotsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  return dispatch(fetchTimeSlotsThunk({ listingId, start, end, timeZone, options })).unwrap();
};

//////////////////////////
// Send Website Inquiry //
//////////////////////////
// Story 4.2.2 — the website "Contact Seller" door POSTs through the
// marketplace's own /api/inquiries proxy to tph-api (screening + masked-pair
// provisioning). This REPLACES the stock sdk.transactions.initiate inquiry
// path, whose only consumer was the orphaned SectionAuthorMaybe — no
// transaction is created on this door and success is confirmed in-modal.

// Mirrors tph-api's `req_` + UUID request_id format. Generated once per submit
// attempt so the backend's duplicate-request_id no-op idempotency covers
// double-fire races beneath the in-flight submit disable.
const generateInquiryRequestId = () => {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : null;
  const uuid =
    cryptoObj && typeof cryptoObj.randomUUID === 'function'
      ? cryptoObj.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
  return `req_${uuid}`;
};

// The AC6 response-state matrix: branch on status + body code/shape — there are
// two distinct 200s (screened vs relayed) and two distinct 409s (R3's body has
// `error` as a flat STRING, R4/R5 use the `error: {code}` envelope). The
// silent-screening invariant is enforced HERE: `blocked` collapses to the SAME
// result as `passed` before anything reaches state, so the two render
// byte-identically downstream by construction.
export const mapWebsiteInquiryResponse = ({ ok, status, body, retryAfter }) => {
  const errorCode = body?.error?.code;
  if (ok && body?.relayed === true) {
    return { kind: 'relayed' }; // R2 — UC7 relay into the existing thread
  }
  if (ok && body?.screening_state === 'held') {
    return { kind: 'held' }; // R1 held — softened success, reveals nothing
  }
  if (ok) {
    return { kind: 'sent' }; // R1 passed AND blocked — identical by construction
  }
  if (status === 409 && body?.error === 'inquiry_screening_in_progress') {
    const parsed = Number.parseInt(retryAfter, 10);
    return {
      kind: 'screening_in_progress', // R3 — calm wait state, not an error
      retryAfterSeconds: Number.isInteger(parsed) ? parsed : 60,
    };
  }
  if (status === 409 && errorCode === 'conversation_already_open') {
    return { kind: 'already_open' }; // R4 — info state, no SID/row-id surface
  }
  if (errorCode === 'listing_unavailable') {
    return { kind: 'listing_unavailable' }; // Story 5.45 — keyed on the code, any non-2xx the proxy relays (4xx)
  }
  if (errorCode === 'phone_not_on_record') {
    return { kind: 'phone_not_on_record' }; // proxy edge — re-run the verify gate
  }
  if (status === 422) {
    return { kind: 'validation_failed' }; // R5
  }
  return { kind: 'error' }; // R6 — truthful failure; never renders as Sent
};

const sendWebsiteInquiryPayloadCreator = async ({ listingUuid, message }, { rejectWithValue }) => {
  try {
    const response = await postWebsiteInquiry({
      listing_uuid: listingUuid,
      message_body: message,
      request_id: generateInquiryRequestId(),
    });
    return mapWebsiteInquiryResponse(response);
  } catch (e) {
    // Transport failure (network down, aborted) — same truthful R6 surface.
    return rejectWithValue({ kind: 'error' });
  }
};

export const sendWebsiteInquiryThunk = createAsyncThunk(
  'ListingPage/sendWebsiteInquiry',
  sendWebsiteInquiryPayloadCreator
);
// Backward compatible wrapper for the thunk
export const sendWebsiteInquiry = (listingUuid, message) => (dispatch, getState, sdk) => {
  return dispatch(sendWebsiteInquiryThunk({ listingUuid, message })).unwrap();
};

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

//////////////////////////////////
// Fetch Transaction Line Items //
//////////////////////////////////
const fetchTransactionLineItemsPayloadCreator = (
  { orderData, listingId, isOwnListing },
  { rejectWithValue }
) => {
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      return response.data;
    })
    .catch(e => {
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
        statusText: e.statusText,
      });
      return rejectWithValue(storableError(e));
    });
};

export const fetchTransactionLineItemsThunk = createAsyncThunk(
  'ListingPage/fetchTransactionLineItems',
  fetchTransactionLineItemsPayloadCreator
);
// Backward compatible wrapper for the thunk
export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  return dispatch(fetchTransactionLineItemsThunk({ orderData, listingId, isOwnListing })).unwrap();
};

// ================ Slice ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendWebsiteInquiryInProgress: false,
  websiteInquiryResult: null,
  inquiryModalOpenForListingId: null,
  // Story 7.18 (ID-2) — set when the loaded listing is unavailable to a
  // non-owner: { id: UUID, similarIds: UUID[], ownerView: boolean,
  // decidedOnServer: boolean } (decidedOnServer: step-6 triage L2). Null for
  // every available listing; replaced or cleared when every showListing read
  // SETTLES (S4: every load re-decides, a reopened listing gets its full page
  // on the next load; ID-29 for why not on pending).
  unavailableListing: null,
  // Step-6 triage (L4, ID-36) — the uuid of the showListing read in flight, or
  // null. The page fires no engagement view while its listing's read is
  // pending: a cached entity from an earlier live visit must not count as a
  // view of a horse that may since have been withdrawn.
  showListingPendingFor: null,
  // Final fix round (F1, ID-52; replaces ID-46's `latestRequestedUuid`) — the
  // RTK requestId of the most recent showListing request. Written ONLY by the
  // pending reducer. A uuid could not tell two reads of ONE listing apart
  // (back/forward, A → B → A), so an older read of it settling last won.
  latestRequestId: null,
};

// Final fix round (F1, F2; ID-52) — is this settling showListing read the most
// recent request? Only that read may clear the decision, write `state.id` or
// `showListingError`, or clear the in-flight marker.
const isLatestRequest = (state, action) =>
  !!state.latestRequestId && state.latestRequestId === action.meta?.requestId;

// Step-6 triage (M1, ID-30) — is this settling showListing read for the listing
// whose unavailable decision the slice holds?
const isReadForHeldDecision = (state, action) => {
  const heldUuid = state.unavailableListing?.id?.uuid;
  return !!heldUuid && heldUuid === action.meta?.arg?.listingId?.uuid;
};

const listingPageSlice = createSlice({
  name: 'ListingPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      // Story 7.18 (ID-29) — the unavailable decision survives this reset.
      // loadData dispatches setInitialValues FIRST, and the router re-runs
      // loadData on client mount, so a plain reset wiped the SSR'd decision
      // and the page flashed "Loading listing…" until the re-read returned.
      // The decision is keyed by listing id (never applies to another listing)
      // and is replaced or cleared when the read settles.
      // Step-6 triage (L4, ID-36) — so does the in-flight read marker: a reset
      // while a read is still out must not unblock the engagement view.
      // Final fix round (F7, ID-56) — the carried fields come AFTER the
      // payload, so no payload can override them.
      return {
        ...initialState,
        ...action.payload,
        unavailableListing: state.unavailableListing,
        showListingPendingFor: state.showListingPendingFor,
        // Re-review triage (R-M1, ID-46) — and so does the latest request: a
        // reset while that read is out must not drop its decision.
        latestRequestId: state.latestRequestId,
      };
    },
    // Clears the last submit outcome when the inquiry modal closes/reopens so a
    // stale confirmation can never greet the next open.
    resetWebsiteInquiry: state => {
      state.sendWebsiteInquiryInProgress = false;
      state.websiteInquiryResult = null;
    },
    // Story 7.18 (ID-2) — the unavailable decision for the current listing.
    // Step-6 triage (L3, ID-35) — a fresh decision for the SAME listing keeps
    // `ownerView`. Without this, a re-read that settles after the owner swap
    // replaced `ownerView: true` with false: full → unavailable → full, and a
    // second ownListings.show. `ownerView` only ever becomes true through a
    // successful ownListings.show (setUnavailableOwnerView), so keeping it for
    // the same id cannot reach a non-owner.
    //
    // Step-6 follow-up (ID-45), re-review triage (R-M1, ID-46) and final fix
    // round (F1, ID-52, operator 2026-10-02) — a decision applies ONLY when it
    // came from the latest showListing REQUEST (`latestRequestId`, written by
    // the pending reducer alone); otherwise it is ignored. Reads can settle in
    // either order on the SPA, and the later listing's page (whose entity is
    // never stored) must end with its own decision or it hangs on "Loading
    // listing…". ID-45 guarded on `state.id`, which the fulfilled reducer also
    // writes; ID-46 on the requested uuid, which cannot tell two reads of one
    // listing apart, so a stale read's decision landed on a reopened horse.
    setUnavailableListing: (state, action) => {
      const prev = state.unavailableListing;
      const { requestId, ...next } = action.payload || {};
      if (!action.payload || !state.latestRequestId || state.latestRequestId !== requestId) {
        return;
      }
      const sameListing = !!prev && !!next && prev.id?.uuid === next.id?.uuid;
      state.unavailableListing =
        sameListing && prev.ownerView ? { ...next, ownerView: true } : next;
    },
    // Step-6 triage (L2, ID-34) — the browser's first loadData has used the SSR
    // decision; every later load re-reads (S4).
    consumeServerUnavailableDecision: state => {
      if (state.unavailableListing) {
        state.unavailableListing.decidedOnServer = false;
      }
    },
    // Story 7.18 (ID-8, D5) — the owner's client fetched the own listing; the
    // page may render the full owner view. Guarded on the id so a late result
    // for a previous listing cannot flip the current one.
    setUnavailableOwnerView: (state, action) => {
      if (state.unavailableListing?.id?.uuid === action.payload?.id?.uuid) {
        state.unavailableListing.ownerView = true;
      }
    },
    // Story 7.18 (ID-3, D4) — reviews of a withdrawn listing's seller must not
    // serialize with the unavailable page. loadData fetches them in parallel
    // with the listing, so they are cleared after both settle.
    clearReviews: state => {
      state.reviews = [];
      state.fetchReviewsError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(showListingThunk.pending, (state, action) => {
        state.id = action.meta.arg.listingId;
        state.showListingError = null;
        state.showListingPendingFor = action.meta.arg.listingId?.uuid ?? null;
        state.latestRequestId = action.meta.requestId;
        // Story 7.18 (ID-29) — the previous decision is NOT cleared here. The
        // router re-runs loadData on client mount (Routes.js, "Calling loadData
        // on initial rendering"), so clearing on pending collapsed the SSR'd
        // unavailable view into "Loading listing…" until the re-read returned —
        // a visible flash on every first load (browser pass, 2026-10-02). The
        // decision is keyed by listing id, so a stale one can never apply to a
        // different listing; it is replaced or cleared when the read settles.
      })
      .addCase(showListingThunk.fulfilled, (state, action) => {
        // Final fix round (F1, ID-52) — an older request settling late changes
        // nothing: not the decision, not `state.id`, not the in-flight marker.
        if (!isLatestRequest(state, action)) {
          return;
        }
        // Step-6 triage (L4, ID-36) — only the settle of the read that set the
        // in-flight marker clears it. Final fix round (ID-54) — that read is the
        // latest request (the gate above); a uuid match let an older read of the
        // SAME listing clear it while the newer read was still out.
        state.showListingPendingFor = null;
        // Data is handled by addMarketplaceEntities in the thunk.
        // Story 7.18 (ID-2) — the unavailable path returns the minimal
        // `{ unavailable: true, id }` payload instead of the sdk response.
        // Story 7.18 (S4, ID-29) — every load re-decides: an AVAILABLE result
        // clears the decision (a reopened listing gets its full page back); the
        // unavailable path has already dispatched the fresh decision.
        // Step-6 triage (M1, ID-30) — only a read FOR the held decision's
        // listing may clear it. Reads can settle out of order on the SPA: a
        // slower available read for listing A must not wipe listing B's
        // decision, or B (whose entity was never stored) spins on "Loading
        // listing…" forever.
        if (action.payload.unavailable) {
          state.id = action.payload.id;
        } else {
          state.id = action.payload.data.data.id;
          if (isReadForHeldDecision(state, action)) {
            state.unavailableListing = null;
          }
        }
      })
      .addCase(showListingThunk.rejected, (state, action) => {
        // Final fix round (F2, ID-55) — nor does an older request's failure: its
        // error used to land on the newer page as NotFound (pre-existing since
        // 7f251f923, the template's RTK port).
        if (!isLatestRequest(state, action)) {
          return;
        }
        state.showListingPendingFor = null;
        state.showListingError = action.payload;
        // Story 7.18 (ID-29) — a failed re-read (a deleted listing's 404) also
        // drops the decision; the error branches render ahead of it anyway.
        // Step-6 triage (M1, ID-30) — and only for the held decision's listing.
        if (isReadForHeldDecision(state, action)) {
          state.unavailableListing = null;
        }
      })
      .addCase(fetchReviewsThunk.pending, state => {
        state.fetchReviewsError = null;
      })
      .addCase(fetchReviewsThunk.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(fetchReviewsThunk.rejected, (state, action) => {
        state.fetchReviewsError = action.payload;
      })
      .addCase(fetchTimeSlotsThunk.pending, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          state.timeSlotsForDate = removeOutdatedDateData(state.timeSlotsForDate);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = null;
          state.timeSlotsForDate[dateId].fetchedAt = null;
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = true;
          state.timeSlotsForDate[dateId].timeSlots = [];
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = null;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = true;
        }
      })
      .addCase(fetchTimeSlotsThunk.fulfilled, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchedAt = new Date().getTime();
          state.timeSlotsForDate[dateId].timeSlots = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].timeSlots = action.payload;
        }
      })
      .addCase(fetchTimeSlotsThunk.rejected, (state, action) => {
        const { options, start, timeZone } = action.meta.arg;
        const { useFetchTimeSlotsForDate = false } = options || {};

        if (useFetchTimeSlotsForDate) {
          const dateId = stringifyDateToISO8601(start, timeZone);
          if (!state.timeSlotsForDate[dateId]) {
            state.timeSlotsForDate[dateId] = {};
          }
          state.timeSlotsForDate[dateId].fetchTimeSlotsInProgress = false;
          state.timeSlotsForDate[dateId].fetchTimeSlotsError = action.payload;
        } else {
          const monthId = monthIdString(start, timeZone);
          if (!state.monthlyTimeSlots[monthId]) {
            state.monthlyTimeSlots[monthId] = {};
          }
          state.monthlyTimeSlots[monthId].fetchTimeSlotsInProgress = false;
          state.monthlyTimeSlots[monthId].fetchTimeSlotsError = action.payload;
        }
      })
      .addCase(sendWebsiteInquiryThunk.pending, state => {
        state.sendWebsiteInquiryInProgress = true;
        state.websiteInquiryResult = null;
      })
      .addCase(sendWebsiteInquiryThunk.fulfilled, (state, action) => {
        state.sendWebsiteInquiryInProgress = false;
        state.websiteInquiryResult = action.payload;
      })
      .addCase(sendWebsiteInquiryThunk.rejected, (state, action) => {
        state.sendWebsiteInquiryInProgress = false;
        state.websiteInquiryResult = action.payload || { kind: 'error' };
      })
      .addCase(fetchTransactionLineItemsThunk.pending, state => {
        state.fetchLineItemsInProgress = true;
        state.fetchLineItemsError = null;
      })
      .addCase(fetchTransactionLineItemsThunk.fulfilled, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.lineItems = action.payload;
      })
      .addCase(fetchTransactionLineItemsThunk.rejected, (state, action) => {
        state.fetchLineItemsInProgress = false;
        state.fetchLineItemsError = action.payload;
      });
  },
});

export const {
  setInitialValues,
  resetWebsiteInquiry,
  setUnavailableListing,
  setUnavailableOwnerView,
  consumeServerUnavailableDecision,
  clearReviews,
} = listingPageSlice.actions;

export default listingPageSlice.reducer;

// ================ Pedigree link lookup ================ //

export const fetchPedigreeLinksThunk = createAsyncThunk(
  'ListingPage/fetchPedigreeLinks',
  async (names, { extra: sdk }) => {
    const uniqueNames = [...new Set(names.filter(Boolean))];
    if (uniqueNames.length === 0) return {};

    const results = await Promise.allSettled(
      uniqueNames.map(name =>
        sdk.listings
          .query({
            keywords: name,
            states: ['published'],
            perPage: 3,
            'fields.listing': ['title', 'publicData'],
          })
          .then(resp => {
            const match = resp.data.data.find(
              l => l.attributes?.publicData?.registeredName?.toLowerCase() === name.toLowerCase()
            );
            return { name, listing: match || null };
          })
          .catch(() => ({ name, listing: null }))
      )
    );

    const linkMap = {};
    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value.listing) {
        const { name, listing } = result.value;
        linkMap[name] = {
          id: listing.id.uuid,
          slug: createSlug(listing.attributes.title),
        };
      }
    });
    return linkMap;
  }
);

// ================ Related horses lookup ================ //

const denormalize = resp => {
  const included = resp.data.included || [];
  const imageMap = {};
  included.forEach(item => {
    if (item.type === 'image') imageMap[item.id.uuid] = item;
  });
  return resp.data.data.map(listing => ({
    ...listing,
    images: (listing.relationships?.images?.data || [])
      .map(ref => imageMap[ref.id.uuid])
      .filter(Boolean),
  }));
};

// Candidate-pool depth for the two keyword-relevance queries (same-sire +
// offspring). Keyword search tokenizes multi-word names, so with a common
// sire name the TRUE matches can rank far below the display cap — staging
// proof: the intended offspring ranked 11 of 42 for "Epic Leader Test" and a
// perPage of 8 never fetched it, so the normalized confirm never saw it.
// This only widens the pool the client-side confirm runs over; the rendered
// count stays capped by the .slice(0, 8) below. Sharetribe allows up to 100.
const RELATED_KEYWORD_FETCH_LIMIT = 40;

// Sire/horse names are seller-typed free text — compare case-insensitively
// with trimmed + collapsed whitespace on BOTH sides. Hardens the same-sire
// tab's previously exact === match and powers the offspring match.
const normalizeHorseName = name =>
  typeof name === 'string'
    ? name
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase()
    : '';

// ── Story 7.18 (ID-6) — the related-horses query blocks, EXTRACTED so the
// unavailable page's similar-horse query reuses the SAME shape. Each helper is
// the thunk's former inline block with its body byte-for-byte; the thunk calls
// them with identical arguments, so the live related-horses tabs are unchanged
// (pinned by ListingPage.relatedHorses.test.js and cardFramingAllowlists.test.js,
// both unedited by 7.18).

// The `imageFields` block — see the Story 12.13 (AC-22) note in the thunk for
// why the aspect ratio arrives as an argument.
const buildRelatedImageFields = aspectRatio => ({
  include: ['images'],
  // 🔴 Story 12.15 — `'publicData'` WHOLE, so `photoFocalPoints` already
  // arrives and no AC-1 allowlist change is needed here. Pinned anyway in
  // `cardFramingAllowlists.test.js`, so a later "tidy-up" that narrows this
  // to a sparse fieldset is caught rather than shipping the related-horses
  // cards inert.
  'fields.listing': ['title', 'price', 'description', 'publicData'],
  'fields.image': ['variants.listing-card', 'variants.listing-card-2x', ...FRAME_IMAGE_FIELDS],
  ...createImageVariantConfig('listing-card', 400, aspectRatio),
  ...createImageVariantConfig('listing-card-2x', 800, aspectRatio),
  ...frameImageVariantConfig(),
  'limit.images': 1,
});

const excludeListing = listingId => l => l.id.uuid !== listingId;
const excludeSold = l => l.attributes?.publicData?.listingStatus !== 'sold';

// on-hold: seller has temporarily paused this listing. Excluded from Related Horses
// so buyers only see immediately available horses in that section.
// On-hold listings remain visible in search results and via direct URL.
const excludeOnHold = l => l.attributes?.publicData?.listingStatus !== 'on_hold';

const disciplineParamOf = disciplines =>
  disciplines?.length > 0 ? `has_any:${disciplines.join(',')}` : null;

const relatedPriceParam = (price, pricingOption) => {
  const hasPriceFilter =
    price?.amount && (pricingOption === 'list_price' || pricingOption === 'asking_price');

  return hasPriceFilter
    ? `${Math.floor(price.amount * 0.5)},${Math.ceil(price.amount * 1.5)}`
    : null;
};

// The raw `sdk.listings.query` the similar block issues, response returned whole.
const fetchSimilarResponse = (sdk, imageFields, extraParams = {}) =>
  sdk.listings.query({
    states: ['published'],
    sort: '-createdAt',
    perPage: 20,
    ...imageFields,
    ...extraParams,
  });

// The similar block's three filters over a denormalised response.
const filterSimilarResponse = (r, exclude) =>
  denormalize(r)
    .filter(exclude)
    .filter(excludeSold)
    .filter(excludeOnHold);

// Step-6 triage (L9, ID-38) — an sdk response cut down to the kept listings and
// the included resources (images, authors) their relationships point at.
const pruneToKept = (response, kept) => {
  const keptUuids = new Set(kept.map(l => l.id?.uuid));
  const listings = (response?.data?.data || []).filter(l => keptUuids.has(l.id?.uuid));
  const referenced = new Set();
  listings.forEach(l =>
    Object.values(l.relationships || {}).forEach(rel => {
      const refs = Array.isArray(rel?.data) ? rel.data : rel?.data ? [rel.data] : [];
      refs.forEach(ref => referenced.add(`${ref.type}/${ref.id?.uuid}`));
    })
  );
  const included = (response?.data?.included || []).filter(r =>
    referenced.has(`${r.type}/${r.id?.uuid}`)
  );
  return { ...response, data: { ...response?.data, data: listings, included } };
};

const querySimilarListings = (sdk, { imageFields, exclude }, extraParams = {}) =>
  fetchSimilarResponse(sdk, imageFields, extraParams).then(r => filterSimilarResponse(r, exclude));

/**
 * Story 7.18 (ID-6, S5, D7, D8) — the similar horses for the UNAVAILABLE page.
 *
 * Reuses the related-horses query SHAPE above (discipline `has_any`, the ±50%
 * price band for `list_price` / `asking_price`, the exclude / sold / on-hold
 * filters, `states: ['published']`, `-createdAt`, `perPage: 20`) and NOT the
 * thunk's fallback chain:
 *   - no breed — S5 names discipline and price band only, and the thunk skips
 *     the band entirely when `breed` is absent;
 *   - D8 — when the band applied, keep only listings whose
 *     `publicData.pricingCurrency` equals the closed listing's effective
 *     currency (`pricingCurrency ?? price.currency`);
 *   - the fallback threshold is `< 3`, not `< 4` — S5 allows 3–4 and three
 *     price-matched horses beat four discipline-only ones;
 *   - query 2 (discipline only, no band, no currency filter) REPLACES query 1's
 *     result, the thunk's own semantics;
 *   - at most 4 are kept; no disciplines → no query at all.
 *
 * S5 "never DISPLAY": the closed listing's discipline, price, band and currency
 * are locals here and in the caller. Nothing stores them.
 *
 * Any query error yields `[]` — the page never fails because of this section.
 *
 * Step-6 triage (L8, ID-37) — query 2 runs only when query 1 carried the price
 * band; without a band, query 1 already IS the discipline-only query, and
 * re-issuing it cost one identical API call.
 *
 * Step-6 triage (L9, ID-38) — only the KEPT listings are stored: the response
 * handed back is the final query's response pruned to the ≤4 kept listings and
 * the included resources they reference. Up to 40 whole listings (both
 * `perPage: 20` responses, filtered-out ones included) used to ride into every
 * unavailable page's SSR state.
 *
 * @returns {Promise<{listings: Array, responses: Array}>} the kept listings
 *   (denormalised, ≤4) and a one-element array holding the pruned sdk response
 *   that carries them (empty when nothing ran), for `addMarketplaceEntities`.
 */
export const querySimilarForUnavailable = async (
  sdk,
  { listingId, disciplines, price, pricingOption, currency, aspectRatio }
) => {
  const disciplineParam = disciplineParamOf(disciplines);
  if (!disciplineParam) return { listings: [], responses: [] };

  const imageFields = buildRelatedImageFields(aspectRatio);
  const exclude = excludeListing(listingId);
  const priceParam = relatedPriceParam(price, pricingOption);
  const responses = [];
  const query = async extraParams => {
    const r = await fetchSimilarResponse(sdk, imageFields, extraParams);
    responses.push(r);
    return filterSimilarResponse(r, exclude);
  };

  try {
    let similar = await query({
      pub_disciplines: disciplineParam,
      ...(priceParam ? { price: priceParam } : {}),
    });
    if (priceParam) {
      // D8 — the API's price band ignores currency; a CAD horse inside a USD
      // band is not "a similar price".
      similar = similar.filter(l => l.attributes?.publicData?.pricingCurrency === currency);
    }
    if (priceParam && similar.length < 3) {
      similar = await query({ pub_disciplines: disciplineParam });
    }
    const kept = similar.slice(0, 4);
    // Query 2 REPLACES query 1, so the kept listings all come from the last
    // response issued.
    return { listings: kept, responses: [pruneToKept(responses[responses.length - 1], kept)] };
  } catch (e) {
    return { listings: [], responses: [] };
  }
};


export const fetchRelatedHorsesThunk = createAsyncThunk(
  'ListingPage/fetchRelatedHorses',
  async (
    {
      listingId,
      authorId,
      sire,
      breed,
      disciplines,
      price,
      pricingOption,
      horseName,
      // 🔴 Story 12.13 (AC-22) — the listing-image aspect, threaded from the
      // caller's `config.layout.listingImage`. It is NOT in scope from
      // `showListingPayloadCreator`'s local `aspectRatio` (that lives in a
      // different function), so it has to arrive as an argument.
      //
      // The default is the config default rather than the old hardcoded `1`:
      // a caller that forgets to thread it now gets the CORRECT 4:3 shape
      // instead of silently reinstating the square-crop defect.
      listingImageAspectRatio = 3 / 4,
    },
    { extra: sdk }
  ) => {
    const aspectRatio = Number.isFinite(listingImageAspectRatio)
      ? listingImageAspectRatio
      : 3 / 4;
    // 🔴 Story 12.13 (AC-22) — DEFECT #0, a live production defect independent
    // of framing.
    //
    // The third argument to `createImageVariantConfig` is the ASPECT RATIO,
    // applied as `variantHeight = Math.round(aspectRatio * width)`
    // (sdkLoader.js:19). A hardcoded `1` therefore asked for 400x400 and
    // 800x800 SQUARE files — which are then displayed in a 4:3 box with
    // `object-fit: cover` (SectionRelatedHorses.module.css:217-230), cropping
    // the photo twice.
    //
    // Measured on 90 real cover photos: the median listing showed 56.5 % of its
    // cover where a correct 4:3 variant shows 99.6 % — 43 percentage points
    // lost for no reason. On a landscape cover (92 % of them) buyers saw 55 %
    // where they could see 96 %.
    //
    // The fix is to derive it from config, exactly as SearchPage.duck.js and
    // ProfilePage.duck.js already do.
    //
    // ⚠️ CORRECTING THE STORY: AC-22 says "ListingPage.duck.js already computes
    // aspectRatio at :55 … it is in scope". It is NOT — that local lives inside
    // `showListingPayloadCreator`, a different function. This thunk therefore
    // takes the ratio as an argument, threaded from both call sites
    // (ListingPageCarousel.js, ListingPageCoverPhoto.js), which already have
    // `config` as a prop.
    //
    // ⚠️ Portrait covers see NO change (the square crop is already the binding
    // one). That is correct and expected — not a failure.
    const imageFields = buildRelatedImageFields(aspectRatio);

    const exclude = excludeListing(listingId);

    const disciplineParam = disciplineParamOf(disciplines);

    const priceParam = relatedPriceParam(price, pricingOption);

    const querySimilar = (extraParams = {}) =>
      querySimilarListings(sdk, { imageFields, exclude }, extraParams);

    // Offspring: listings whose free-text publicData.sire names THIS horse.
    // Mirrors the bySire pattern — keyword query (no indexed schema on sire)
    // + normalized client-side confirm.
    const normalizedHorseName = normalizeHorseName(horseName);

    const [sireResp, sellerResp, offspringResp] = await Promise.allSettled([
      sire
        ? sdk.listings
            .query({
              keywords: sire,
              states: ['published'],
              perPage: RELATED_KEYWORD_FETCH_LIMIT,
              ...imageFields,
            })
            .then(r =>
              denormalize(r)
                .filter(exclude)
                .filter(excludeSold)
                .filter(excludeOnHold)
                .filter(l => normalizeHorseName(l.attributes?.publicData?.sire) === normalizeHorseName(sire))
                .slice(0, 8)
            )
        : Promise.resolve([]),
      authorId
        ? sdk.listings
            .query({ authorId, states: ['published'], perPage: 9, ...imageFields })
            .then(r =>
              denormalize(r)
                .filter(exclude)
                .filter(excludeSold)
                .filter(excludeOnHold)
                .slice(0, 8)
            )
        : Promise.resolve([]),
      normalizedHorseName
        ? sdk.listings
            .query({
              keywords: horseName.trim(),
              states: ['published'],
              perPage: RELATED_KEYWORD_FETCH_LIMIT,
              ...imageFields,
            })
            .then(r =>
              denormalize(r)
                .filter(exclude)
                .filter(excludeSold)
                .filter(excludeOnHold)
                .filter(l => normalizeHorseName(l.attributes?.publicData?.sire) === normalizedHorseName)
                .slice(0, 8)
            )
        : Promise.resolve([]),
    ]);

    // Primary: discipline + breed + price range (if applicable)
    let similar = [];
    if (disciplineParam && breed) {
      const primaryParams = {
        pub_disciplines: disciplineParam,
        pub_breed: breed,
        ...(priceParam ? { price: priceParam } : {}),
      };
      similar = await querySimilar(primaryParams).catch(() => []);
    }

    // First fallback: drop price, keep discipline + breed
    if (similar.length < 4 && disciplineParam && breed) {
      similar = await querySimilar({ pub_disciplines: disciplineParam, pub_breed: breed }).catch(
        () => []
      );
    }

    // Second fallback: discipline only
    if (similar.length < 4 && disciplineParam) {
      similar = await querySimilar({ pub_disciplines: disciplineParam }).catch(() => []);
    }

    return {
      bySire: sireResp.status === 'fulfilled' ? sireResp.value : [],
      bySeller: sellerResp.status === 'fulfilled' ? sellerResp.value : [],
      offspring: offspringResp.status === 'fulfilled' ? offspringResp.value : [],
      similar: similar.slice(0, 20),
    };
  }
);

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);

  // Step-6 triage (L2, ID-34) — the router re-runs loadData when the page mounts
  // in the browser (Routes.js, "Calling loadData on initial rendering"). When the
  // SSR read already decided THIS listing is unavailable, that re-run would put
  // the closed listing's full JSON in the visitor's network log and send its
  // discipline and price band in the similar query's URL, on every visit. The
  // SSR read was live (S4), so the first browser load uses it once; SPA
  // navigation and every later load re-read as before. The owner swap is a
  // separate effect in each page variant and is unaffected.
  const held = state.ListingPage.unavailableListing;
  if (!hasNoViewingRights && held?.decidedOnServer && held.id?.uuid === listingId.uuid) {
    dispatch(consumeServerUnavailableDecision());
    return Promise.resolve();
  }

  const promises = hasNoViewingRights
    ? // If user has no viewing rights, only allow fetching their own listing without reviews
      [dispatch(showListing(listingId, config, true))]
    : // For users with viewing rights, fetch the listing and the associated reviews
      [dispatch(showListing(listingId, config)), dispatch(fetchReviews(listingId))];

  // allSettled, NOT all. Both calls above unwrap(), so both reject on an API
  // error, and a malformed listing id (`/l/null`) 400s BOTH of them. Promise.all
  // settles on whichever rejects FIRST and abandons the other — and the server's
  // data loader responds to that rejection by reading the store immediately
  // (server/dataLoader.js). When fetchReviews won that race, the read happened
  // before showListingThunk.rejected had dispatched, so showListingError was
  // still null, ListingPage fell through to its loading branch, and a URL that
  // can never resolve was served as HTTP 200. Intermittent by nature: in
  // production the same request returned 404 or 200 depending on which of the
  // two 400s came back first.
  //
  // allSettled waits for every promise, so every reducer has run before anything
  // downstream observes the store. The rejection contract below is unchanged.
  return Promise.allSettled(promises).then(results => {
    const [listingResult] = results;

    // Still reject when the listing itself could not be loaded — callers relied
    // on that — but only now, once the error is guaranteed to be in the store.
    if (listingResult.status === 'rejected') {
      throw listingResult.reason;
    }

    // Story 7.18 (ID-3, D4) — for an unavailable listing, drop the reviews that
    // were fetched in parallel (they carry the seller's reviewers as authors).
    // Sequencing reviews after the listing would add latency to EVERY live
    // page; one reducer here costs nothing.
    if (getState().ListingPage.unavailableListing?.id?.uuid === listingId.uuid) {
      dispatch(clearReviews());
    }

    // Preserve the previous resolved shape: the raw values, not the allSettled
    // wrappers. Anything that rejected while the listing succeeded (a reviews
    // failure on a listing that loads fine) records its own error in the store
    // and surfaces here as undefined, exactly as an unread Promise.all element.
    const response = results.map(result =>
      result.status === 'fulfilled' ? result.value : undefined
    );

    const listingResponse = response[0];
    const listing = listingResponse?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    if (isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
      // Fetch timeSlots if the user has viewing rights.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }
    return response;
  });
};
